import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

import { PLANS } from "../data/pricingPlans";
import Navbar from "../components/landingpage/Navbar";
import Footer from "../components/landingpage/Footer";
import { BRAND_LIGHT, Bullet, ButtonLink, Eyebrow, INK, Reveal, WRAP } from "../components/landingpage/ui";

function PlanCard({ plan, index }) {
  const dark = plan.recommended;
  return (
    <Reveal delay={index * 110} className="h-full">
      <div
        className={`relative flex flex-col h-full p-8 border rounded-xl shadow-sm ${
          dark ? "border-[#009DB9]" : "bg-white border-slate-300"
        }`}
        style={dark ? { background: INK } : undefined}
      >
        {plan.recommended && (
          <span
            className="absolute px-3 py-1 text-[11px] font-bold tracking-[0.14em] uppercase rounded-md -top-3 left-8"
            style={{ background: BRAND_LIGHT, color: INK }}
          >
            Recommended
          </span>
        )}

        <h2 className={`text-[22px] font-bold ${dark ? "text-white" : "text-[#061A21]"}`}>
          {plan.name}
        </h2>
        <p className={`mt-2 text-[15px] leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
          {plan.tagline}
        </p>

        <div className={`h-px my-6 ${dark ? "bg-white/10" : "bg-slate-200"}`} />

        <h3 className={`text-[14px] font-bold ${dark ? "text-white" : "text-[#061A21]"}`}>
          {plan.featuresTitle}
        </h3>
        <ul className="mt-4 mb-8 space-y-3.5">
          {plan.features.map((f) => (
            <Bullet key={f} dark={dark}>
              {f}
            </Bullet>
          ))}
        </ul>

        <ButtonLink href={plan.cta.href} variant={dark ? "primary" : "outline"} className="w-full mt-auto">
          {plan.cta.label} <ArrowRight size={16} />
        </ButtonLink>
      </div>
    </Reveal>
  );
}

export default function PricingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#EAF0F2] text-[#061A21]">
      <Navbar scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <main>
        {/* ═════════════ HERO (background image + dark overlay) ═════════════ */}
        <section
          id="top"
          className="relative overflow-hidden bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: "url('/pricing-image.jpg')", // ← point this to your image
          }}
        >
          {/* Dark overlay so the white text stays readable over any image */}
          <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

          <div className={`${WRAP} relative z-10 pt-16 pb-40 text-center sm:pt-20 sm:pb-44`}>
            <Reveal>
              <Eyebrow dark>Pricing</Eyebrow>
              <h1 className="max-w-4xl mx-auto mt-6 text-[36px] font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[60px]">
                Fair pricing for <br className="hidden sm:block" />
                <span style={{ color: BRAND_LIGHT }}>faster growth.</span>
              </h1>
              <p className="max-w-2xl mx-auto mt-6 text-[17px] sm:text-[19px] leading-relaxed text-slate-300">
                Flexible pricing plans to suit businesses of all sizes. Pick the one that fits your team and bring
                every company, deal and conversation into one place.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═════════════ PLANS (cards overlap the hero) ═════════════ */}
        <section aria-label="Plans" className="relative z-10 pb-24 -mt-28 sm:-mt-32">
          <div className={WRAP}>
            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
              {PLANS.map((plan, i) => (
                <PlanCard key={plan.id} plan={plan} index={i} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}